/**
 * Briefing API endpoints - daily, weekly, monthly briefings, dashboard
 */
import { fetchApi } from './apiBase';

// === Types ===

export interface BriefingEvent {
  id: string;
  time: string;
  time_display?: string;
  end_time?: string;
  end_time_display?: string | null;
  title: string;
  member_name: string | null;
  location: string | null;
  address?: string | null;
  guardian_note: string | null;
  context_notes: string[];
  is_past: boolean;
  is_current: boolean;
  travel_time_minutes?: number | null;
  source?: string;
  plan_name?: string | null;
  plan_id?: number | null;
  itinerary_item_id?: number | null;
  activity_status?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  event_status?: string | null;
  calendar_name?: string | null;
  calendar_color?: string | null;
}

export interface BriefingTomorrowPreview {
  summary: string;
  event_count?: number;
}

export interface BriefingReminder {
  id: string;
  text: string;
  category: string;
  priority: string;
  due_date: string | null;
  is_overdue: boolean;
  member_name?: string | null;
}

export interface BriefingWeatherImpact {
  has_outdoor_events: boolean;
  weather_warning?: string;
  suggestion?: string;
  affected_events?: string[];
}

export interface BriefingSpecialDate {
  type: 'birthday' | 'anniversary' | 'holiday' | 'school_break' | 'custom';
  title: string;
  date: string;
  days_away: number;
  member_name?: string;
  note?: string;
}

export interface BriefingRenewalItem {
  id: string;
  item_type: 'passport' | 'license' | 'registration' | 'subscription' | 'membership' | 'insurance' | 'other';
  description: string;
  expiration_date: string;
  days_until_expiration: number;
  member_name?: string;
  renewal_url?: string;
  notes?: string;
}

export interface TripModeData {
  is_traveling?: boolean;
  upcoming_trip?: {
    trip_id: number;
    trip_title: string;
    destination: string;
    start_date: string;
    days_until: number;
    message: string;
  };
}

export interface DailyBriefingData {
  date: string;
  greeting: string;
  weather_code?: number | null;
  weather_high?: number | null;
  weather_low?: number | null;
  weather?: {
    summary: string;
    high: number;
    low: number;
    conditions: string;
  };
  events: BriefingEvent[];
  tomorrow_preview?: BriefingTomorrowPreview;
  reminders: BriefingReminder[];
  weather_impacts?: BriefingWeatherImpact;
  special_dates: BriefingSpecialDate[];
  renewal_items: BriefingRenewalItem[];
  trip_mode?: TripModeData;
}

export interface WeeklyDayEvent {
  id: string;
  time?: string;
  start_time: string;
  end_time?: string;
  title: string;
  member_name?: string;
  location?: string;
  event_type?: string;
}

export interface WeeklyDaySummary {
  date: string;
  day_name: string;
  day_number?: number;
  is_today: boolean;
  intensity?: string;
  intensity_label?: string;
  summary?: string;
  weather_code?: number | null;
  weather_high?: number | null;
  weather_low?: number | null;
  event_count: number;
  events: WeeklyDayEvent[];
  special_notes?: string[];
  weather?: {
    high: number;
    low: number;
    conditions: string;
  };
}

export interface NeedsAttentionItem {
  type: 'conflict' | 'unconfirmed' | 'missing_info' | 'reminder';
  title: string;
  description: string;
  action_url?: string;
}

export interface WeeklyBriefingData {
  week_start: string;
  week_end: string;
  start_date: string;
  end_date: string;
  days: WeeklyDaySummary[];
  needs_attention: NeedsAttentionItem[];
}

export interface MonthlyWeekEvent {
  date: string;
  title: string;
  member_name?: string;
  event_type?: string;
}

export interface MonthlyWeekSummary {
  week_number: number;
  start_date: string;
  end_date: string;
  week_label?: string;
  summary?: string;
  event_count: number;
  highlights: MonthlyWeekEvent[];
  events?: MonthlyWeekEvent[];
  key_events?: MonthlyWeekEvent[];
}

export interface MonthlyKeyDate {
  date: string;
  title: string;
  type: 'event' | 'deadline' | 'birthday' | 'holiday';
  member_name?: string;
}

export interface MonthlyToArrangeItem {
  id: string;
  text?: string;
  status_text?: string;
  title: string;
  suggested_date_range?: string;
  priority: 'low' | 'normal' | 'high';
  notes?: string;
}

export interface MonthlyBriefingData {
  month: number;
  year: number;
  month_name: string;
  weeks: MonthlyWeekSummary[];
  key_dates: MonthlyKeyDate[];
  to_arrange: MonthlyToArrangeItem[];
}

// === Dashboard ===

export interface DashboardEvent {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  location?: string;
  event_type: string;
  status: string;
  all_day: boolean;
  family_member_id?: number;
  family_member_name?: string;
  source: 'vizday' | 'subscribed' | 'google' | 'microsoft';
  calendar_color?: string;
}

export interface DashboardMember {
  id: number;
  name: string;
  role: string;
  events: DashboardEvent[];
}

export interface DashboardData {
  date: string;
  members: DashboardMember[];
  shared_events: DashboardEvent[];
  total_events: number;
}

export async function getDashboard(date?: string): Promise<DashboardData> {
  const query = date ? `?date=${date}` : '';
  return fetchApi<DashboardData>(`/api/calendars/dashboard/${query}`);
}

// === Daily Briefing (with localStorage cache) ===

const BRIEFING_CACHE_PREFIX = 'vizday_briefing_';
const BRIEFING_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// Bumped by clearBriefingCache. A request that started before a clear does not
// write its (possibly pre-clear) response into the cache.
let briefingCacheGeneration = 0;

/**
 * Drop every daily briefing held in localStorage. Called after a calendar is
 * switched on or off: the cached payload still carries the old calendar's
 * events, and the network-error fallback below would otherwise serve it.
 */
export function clearBriefingCache(): void {
  briefingCacheGeneration += 1;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(BRIEFING_CACHE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch { /* storage unavailable — nothing cached to clear */ }
}

function getCachedBriefing(date: string): DailyBriefingData | null {
  try {
    const raw = localStorage.getItem(`${BRIEFING_CACHE_PREFIX}${date}`);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached._cachedAt > BRIEFING_CACHE_TTL) {
      localStorage.removeItem(`${BRIEFING_CACHE_PREFIX}${date}`);
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
}

function cacheBriefing(date: string, data: DailyBriefingData): void {
  try {
    localStorage.setItem(`${BRIEFING_CACHE_PREFIX}${date}`, JSON.stringify({
      data,
      _cachedAt: Date.now(),
    }));
  } catch { /* quota exceeded — ignore */ }
}

export async function getDailyBriefing(date?: string): Promise<DailyBriefingData> {
  const now = new Date();
  const dateKey = date || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const query = date ? `?date=${date}` : '';
  const generation = briefingCacheGeneration;

  try {
    const data = await fetchApi<DailyBriefingData>(`/api/calendars/briefing/${query}`, {}, 10000);
    if (generation === briefingCacheGeneration) cacheBriefing(dateKey, data);
    return data;
  } catch (err) {
    // Only fall back to cache for network/timeout errors, not auth or server errors
    const isNetworkError = err instanceof TypeError
      || (err instanceof Error && (err.message.includes('timeout') || err.message.includes('abort') || err.message === 'Failed to fetch'));
    if (isNetworkError) {
      const cached = getCachedBriefing(dateKey);
      if (cached) return cached;
    }
    throw err;
  }
}

// === Weekly Briefing ===

export async function getWeeklyBriefing(startDate?: string): Promise<WeeklyBriefingData> {
  const query = startDate ? `?start_date=${startDate}` : '';
  return fetchApi<WeeklyBriefingData>(`/api/calendars/briefing/weekly/${query}`);
}

// === Monthly Briefing ===

export async function getMonthlyBriefing(month?: number, year?: number): Promise<MonthlyBriefingData> {
  const params = new URLSearchParams();
  if (month !== undefined) params.append('month', month.toString());
  if (year !== undefined) params.append('year', year.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<MonthlyBriefingData>(`/api/calendars/briefing/monthly/${query}`);
}

// === Query ===

export interface QueryEventResult {
  id: string;
  title: string;
  date: string;
  time?: string;
  end_time?: string;
  location?: string;
  member_name?: string;
  event_type?: string;
  status?: string;
}

export interface QueryItinerary {
  id: number;
  title: string;
  destination?: string;
  start_date: string;
  end_date?: string;
  items: {
    date: string;
    time?: string;
    type: string;
    name: string;
    location?: string;
    address?: string;
    duration?: string;
    notes?: string;
  }[];
}

export interface QueryBriefing {
  summary?: string;
  highlights?: string[];
  weather?: {
    condition: string;
    high?: number;
    low?: number;
  };
  events_by_member?: Record<string, BriefingEvent[]>;
  reminders?: string[];
}

export interface QueryResponse {
  query: string;
  query_type: 'date' | 'search' | 'week' | 'month' | 'planning' | 'recommendation' | 'general';
  response_type: 'daily' | 'weekly' | 'monthly' | 'event_list' | 'plan' | 'answer';
  target_date?: string;
  briefing_type?: 'daily' | 'weekly';
  briefing?: QueryBriefing;
  events?: QueryEventResult[];
  search_term?: string;
  family_member?: string;
  planning_request?: string;
  itinerary?: QueryItinerary;
  answer?: string;
}

export async function submitQuery(query: string): Promise<QueryResponse> {
  return fetchApi<QueryResponse>('/api/calendars/query/', {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}
